import VectorSource from 'ol/source/Vector';
import Vector from 'ol/layer/Vector';
import OLDraw from 'ol/interaction/Draw';
import { unByKey as unByKeyOl } from 'ol/Observable';

import { TooltipOverlay } from 'cadenza/map/tooltip/tooltip-overlay';
import { assert, assertNonNullable } from 'cadenza/utils/custom-error';

import type { Feature, Map } from 'ol';
import type { StyleLike } from 'ol/style/Style';
import type { Geometry } from 'ol/geom';
import type { DrawEvent } from 'ol/interaction/Draw';
import type { EventsKey } from 'ol/events';

import i18n from './interactions.properties';

export type MeasureType = 'LineString' | 'Polygon';

export const MEASURE_TOOLTIPS_FEATURE_PROPERTY = 'measurement-tooltips';

export interface Options {
  /**
   * Should the measurement be cleared when starting another one. Defaults to true.
   */
  autoClear?: boolean;
  /**
   * Provide an external Source where measures are added to.
   */
  source?: VectorSource;
}

/**
 * Interaction to do measurements on the map.
 * Depending on the geometry type passed on creation (LineString or Polygon) it will measure the
 * distance between the points in a line, or the area of a polygon.
 * The user adds points by clicking on the map.
 * The value measured is shown on a tooltip in a map overlay.
 */
export abstract class Measure extends OLDraw {

  readonly #map;
  readonly #vectorLayer;
  readonly #source;
  readonly #helpTooltip;
  readonly #style;
  readonly #autoClear;
  #olEventKeys?: EventsKey[];
  #geometryEventKey?: EventsKey;
  #isMeasuring: boolean = false;
  #measureTooltip?: TooltipOverlay;
  #measure?: Feature;

  protected constructor (map: Map, type: MeasureType, style: StyleLike, options: Options = {}) {
    const source = options.source ?? new VectorSource();
    super({ type, source, style });
    this.setActive(false);

    this.#map = map;
    this.#style = style;
    this.#autoClear = options.autoClear ?? true;
    this.#source = source;
    if (!options.source) {
      this.#vectorLayer = new Vector({
        map,
        source
      });
    }

    this.#helpTooltip = new TooltipOverlay(map, {
      positioning: 'center-right',
      message: i18n('measure.start'),
      showArrow: false
    });

    this.on('change:active', event => {
      if (!event.oldValue) {
        this.onActivation();
      } else {
        this.onDeactivation();
      }
    });
  }

  protected onActivation () {
    this.#vectorLayer?.setMap(this.#map);
    this.#helpTooltip.activate();
    this.#olEventKeys = [
      this.on('drawstart', (event) => this.onMeasureStart(event)),
      this.on('drawend', (event) => this.onMeasureEnd(event)),
      this.on('drawabort', () => this.onMeasureAbort())
    ];
    this._showHelpTooltipOnNextPointerMove();
  }

  protected onDeactivation () {
    if (this.#isMeasuring) {
      this.onMeasureAbort();
    }
    if (this.#autoClear && this.measure) {
      this.clearMeasure();
    }
    this.#vectorLayer?.setMap(null);
    this.#helpTooltip.deactivate();
    unByKeyOl(this.#olEventKeys!);
  }

  _showHelpTooltipOnNextPointerMove () {
    this.#olEventKeys!.push(
      this.#map.once('pointermove', () => {
        if (this.#isMeasuring) {
          return;
        }
        this.#helpTooltip.show();
      })
    );
  }

  protected onMeasureStart (event: DrawEvent) {
    if (this.#autoClear && this.measure) {
      this.clearMeasure();
    }
    this.#bindOnGeometryChange(event);
    const feature = event.feature;
    feature.set(MEASURE_TOOLTIPS_FEATURE_PROPERTY, []);
    feature.setStyle(this.#style);
    this.#measure = feature;
    this.#isMeasuring = true;
    this.#helpTooltip.hide();

    const measureTooltip = new TooltipOverlay(this.map, {
      positioning: 'top-center',
      showArrow: true,
      followMouse: false
    });
    this.attachMeasureOverlayToMeasure(measureTooltip);
    measureTooltip.activate();
    measureTooltip.show();
    this.#measureTooltip = measureTooltip;
    this.updateMeasure(measureTooltip, feature.getGeometry()!);
  }

  protected attachMeasureOverlayToMeasure (tooltipOverlay: TooltipOverlay) {
    const measure = this.#measure;
    assertNonNullable(measure);
    attachMeasureOverlayToFeature(measure, tooltipOverlay);
  }

  protected removeMeasureOverlayFromMeasure (tooltipOverlay: TooltipOverlay) {
    const measure = this.#measure;
    assertNonNullable(measure);
    const tooltips = measure.get(MEASURE_TOOLTIPS_FEATURE_PROPERTY) as TooltipOverlay[];
    const index = tooltips.indexOf(tooltipOverlay);
    assert(index !== -1, 'Tooltip not present in current measure feature.');
    tooltips.splice(index, 1);
  }

  protected onMeasureEnd (event: DrawEvent) {
    this.#unbindOnGeometryChange();
    this.#isMeasuring = false;
    this._showHelpTooltipOnNextPointerMove();
  }

  protected onMeasureAbort () {
    this.clearMeasure();
    this.#unbindOnGeometryChange();
    this.#isMeasuring = false;
    this._showHelpTooltipOnNextPointerMove();
  }

  protected clearMeasure () {
    const measure = this.measure;
    assertNonNullable(measure);
    clearMeasureOverlaysForFeature(measure);
    this.source.removeFeature(measure);
    this.#measure = undefined;
    this.#measureTooltip = undefined;
  }

  #bindOnGeometryChange (drawStartEvent: DrawEvent) {
    this.#geometryEventKey = drawStartEvent.feature.getGeometry()!
      .on('change', event => this.updateMeasure(this.measureTooltip!, event.target));
  }

  #unbindOnGeometryChange () {
    unByKeyOl(this.#geometryEventKey!);
  }

  protected abstract updateMeasure (measure: TooltipOverlay, geometry: Geometry): void;

  get map () {
    return this.#map;
  }

  get measureTooltip () {
    return this.#measureTooltip;
  }

  get source () {
    return this.#source;
  }

  get measure () {
    return this.#measure;
  }

}

/**
 * Attaches a tooltip to a feature.
 *
 * @param feature
 * @param measureOverlay
 */
export function attachMeasureOverlayToFeature (feature: Feature, measureOverlay: TooltipOverlay) {
  const overlays = feature.get(MEASURE_TOOLTIPS_FEATURE_PROPERTY) as TooltipOverlay[];
  if (!overlays) {
    feature.set(MEASURE_TOOLTIPS_FEATURE_PROPERTY, [ measureOverlay ]);
    return;
  }
  overlays.push(measureOverlay);
}

/**
 * Clears all measure tooltips for a given feature. Not only deactivates the Tooltip, but also detaches it from the feature.
 *
 * @param feature
 */
export function clearMeasureOverlaysForFeature (feature: Feature) {
  const overlays = feature.get(MEASURE_TOOLTIPS_FEATURE_PROPERTY) as TooltipOverlay[] | undefined;
  if (!overlays) {
    return;
  }
  let overlay = overlays.pop();
  while (overlay) {
    overlay.deactivate();
    overlay = overlays.pop();
  }
}

/**
 * Removes all measure overlays for a single feature from the map, i.e. deactivates all measure tooltips on a feature, if any.
 * The overlays are still attached to the feature and thus can be reactivated.
 *
 * @param feature
 */

export function removeMeasureOverlaysFromMapForFeature (feature: Feature) {
  const measureTooltips = feature.get(MEASURE_TOOLTIPS_FEATURE_PROPERTY);
  if (!measureTooltips) {
    return;
  }
  (measureTooltips as TooltipOverlay[]).forEach(tooltip => tooltip.deactivate());
}

/**
 * Removes all measure overlays on all features for a given source, i.e. deactivates all measure tooltips for a given source.
 * The overlays are still attached to the feature and thus can be reactivated.
 *
 * @param source
 */
export function removeMeasureOverlaysForSource (source: VectorSource) {
  getAllMeasureTooltipsForSource(source).forEach(tooltip => tooltip.deactivate());
}

/**
 * Sets the visibility on all measure tooltips for a given source to the provided value.
 *
 * @param source
 * @param visible
 */
export function setMeasureOverlaysVisible (source: VectorSource, visible: boolean) {
  getAllMeasureTooltipsForSource(source).forEach(tooltipOverlay => {
    tooltipOverlay.visible = visible;
  });
}

function getAllMeasureTooltipsForSource (source: VectorSource) {
  return source.getFeatures()
    .map(feature => feature.get(MEASURE_TOOLTIPS_FEATURE_PROPERTY))
    .filter(property => property !== undefined)
    .flatMap(property => property as TooltipOverlay[]);
}
